/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : AiSuggestionDecisionMatrix.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import React, { useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  LinearProgress,
} from "@mui/material";
import AISuggestionLoader from "../../../../components/customComponentsV2/SmartMatchingDialogContainer/AiSuggestionLoader";
import smartMatchActions from "../../../../redux/actions/smart-matching/smartMatchSuggestionActions";
import { useDispatch } from "react-redux";
import { HandleApiActions, profileActions } from "../../../../redux/actions";
import CustomButtonV2 from "../../../../components/customComponentsV2/common/button/CustomButtonV2";
import { toast } from "react-toastify";

function AiSuggestionDecisionMatrix({
  entityDetails,
  setOpen,
  setCreateTicketData,
  uploads,
  setUploads,
  selectedTicket,
}) {
  const dispatch = useDispatch();

  const [apiParams, setApiParams] = React.useState(null);
  const [status, setStatus] = React.useState("loading");
  const [data, setData] = React.useState(null);
  const [selectedFiles, setSelectedFiles] = React.useState({});
  const [loadingMessage, setLoadingMessage] = React.useState(
    "Preparing AI suggestions...",
  );

  const getRecommendationKey = (rec, fallback = "0") =>
    `${selectedTicket?.intr_id || entityDetails?.id || "intr"}-${
      rec?.target?.entityId || fallback
    }`;

  const getArtifactName = (rec) => {
    if (rec?.targetContext?.number) return rec.targetContext.number;
    if (rec?.target?.entityId) {
      // return `${rec?.target?.entityType || "ENTITY"}-${rec.target.entityId}`;
      return `Simulation File`;
    }
    return "Unknown Artifact";
  };

  const normalizeSuggestionResponse = (resp) => {
    if (!resp) return null;

    if (typeof resp === "string") {
      const lines = resp.split("\n");
      let finalPayload = null;
      lines.forEach((line) => {
        const trimmed = line.trim();
        if (!trimmed.startsWith("data:")) return;
        const raw = trimmed.slice(5).trim();
        if (!raw) return;
        try {
          const evt = JSON.parse(raw);
          if (evt?.stage === "FINAL_RESULT" && evt?.payload) {
            finalPayload = evt.payload;
          }
        } catch (e) {
          console.warn("Failed to parse SSE line:", raw);
        }
      });
      return finalPayload;
    }

    if (resp?.stage === "FINAL_RESULT" && resp?.payload) {
      return resp.payload;
    }

    if (typeof resp?.data === "string") {
      return normalizeSuggestionResponse(resp.data);
    }

    if (resp?.data?.stage === "FINAL_RESULT" && resp?.data?.payload) {
      return resp.data.payload;
    }

    return resp;
  };

  const parseSseEvents = (value) => {
    if (typeof value !== "string") return [];
    const events = [];
    value.split("\n").forEach((line) => {
      const trimmed = line.trim();
      if (!trimmed.startsWith("data:")) return;
      const raw = trimmed.slice(5).trim();
      if (!raw) return;
      try {
        events.push(JSON.parse(raw));
      } catch (e) {
        console.warn("Failed to parse SSE line:", raw);
      }
    });
    return events;
  };

  const getStageLoadingMessage = (evt) => {
    const stageMap = {
      VALIDATION: "Validating request...",
      SEMANTIC_CHECK: "Applying semantic similarity...",
      HISTORIC_CHECK: "Checking historic linkages...",
      CONTEXTUAL_APPLICABILITY: "Checking contextual applicability...",
      RANKING: "Ranking recommendations...",
      FINAL_RESULT: "Finalizing recommendation response...",
    };

    if (evt?.message) return evt.message;
    if (evt?.stage && stageMap[evt.stage]) return stageMap[evt.stage];
    return "Processing AI suggestions...";
  };

  /**
   * Fetch AI Suggestions
   */
  const fetchSuggestions = async () => {
    setLoadingMessage("Starting recommendation pipeline...");

    const resp = await dispatch(
      HandleApiActions({
        https_method: "POST",
        ...smartMatchActions?.get_suggestions,
        params: apiParams,
        show_toast: false,
        ignore_app_loading_spinner: true,
      }),
    );

    console.log("API Response:", resp);

    const rawSse = typeof resp === "string" ? resp : resp?.data;
    const events = parseSseEvents(rawSse);
    if (events.length) {
      events.forEach((evt) => {
        if (evt?.status === "IN_PROGRESS" || evt?.status === "COMPLETED") {
          setLoadingMessage(getStageLoadingMessage(evt));
        }
      });
    }

    const normalizedResp = normalizeSuggestionResponse(resp);

    if (normalizedResp?.error) {
      setStatus("error");
    } else if (
      normalizedResp?.queue === true ||
      !normalizedResp?.recommendations ||
      normalizedResp.recommendations.length === 0
    ) {
      setStatus("empty");
    } else {
      setData(normalizedResp);
      setStatus("success");
    }
  };

  /**
   * Trigger API Call
   */
  useEffect(() => {
    if (apiParams) {
      fetchSuggestions();
    }
  }, [apiParams]);

  /**
   * When entity changes
   */
  useEffect(() => {
    if (!entityDetails) {
      setApiParams(null);
      setStatus("error");
      return;
    }

    setStatus("loading");
    setLoadingMessage("Preparing request...");

    setApiParams({
      source: {
        entityType: "INTERSECTION",
        entityId: entityDetails?.id || 0,
      },
      intent: "SMART_MATCH",
      scope: entityDetails?.scope || null,
      filters: {
        targetEntityTypes: ["ARTEFACT", "ATTACHMENT"],
        excludeExistingLinks: true,
      },
      options: {
        topK: entityDetails?.topK || 5,
        minConfidence: entityDetails?.minConfidence || 0.1,
        explain: true,
      },
    });
  }, [entityDetails]);

  /* ---------------- FILE UPLOAD ---------------- */

  const handleFileUpload = async (rec, file) => {
    if (!file) return;

    const res = await dispatch(
      HandleApiActions({
        ...profileActions.upload_profile_pic,
        show_toast: false,
        file: file,
        is_file: true,
      }),
    );

    if (!res?.error) {
      const recKey = getRecommendationKey(rec, Math.random().toString());
      const uploadedFile = {
        name: file.name,
        filename: res?.file_name,
        url: res?.file_url || res?.file_path || null, // depends on backend
        reference_id:
          rec?.target?.entityId || rec?.targetContext?.referenceId || null,
        type: "ATTACHMENT",
        // type: rec?.target?.entityType || rec?.targetContext?.entityType || null,
        // attachment_type: rec?.targetContext?.number || null,
        attachment_type: "SIMULATION",
      };

      console.log("Uploaded File Details:", uploadedFile);
      const intrId = recKey;
      console.log("Uploaded File Details:", rec);

      setUploads((prev) => ({
        ...prev,
        [intrId]: {
          ...prev[intrId],
          progress: 100,
          uploading: false,
          uploaded: true,
        },
      }));

      setCreateTicketData((prev) =>
        prev.map((item) => {
          if (item.intr_id == entityDetails?.id) {
            return {
              ...item,
              artifacts: [uploadedFile],
            };
          }
          return item;
        }),
      );

      setSelectedFiles((prev) => ({
        ...prev,
        [intrId]: uploadedFile,
      }));
    }
  };

  const handleFileChange = (rec, file) => {
    console.log(rec, "recrec");
    // Get the file extension
    const fileExtension = file.name.split(".").pop().toLowerCase();
    const allowedExtensions = ["jpg", "jpeg", "png"];

    // Check file size (800KB = 800 * 1024 bytes)
    const maxSize = 800 * 1024;

    if (!allowedExtensions.includes(fileExtension)) {
      toast.dark("Please select a valid file (JPG or PNG)", {
        type: "error",
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    if (file.size > maxSize) {
      toast.dark("File size should not exceed 800KB", {
        type: "error",
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }
    const intrId = getRecommendationKey(rec, Math.random().toString());
    console.log(intrId, "intrId");

    setSelectedFiles((prev) => ({
      ...prev,
      [intrId]: {
        name: file.name,
        url: URL.createObjectURL(file), // instant preview
      },
    }));
    setUploads((prev) => ({
      ...prev,
      [intrId]: {
        name: file.name,
        progress: 10,
        uploading: true,
        uploaded: false,
      },
    }));
    handleFileUpload(rec, file);
  };

  console.log(selectedFiles, "selectedFiles");
  console.log(uploads, "uploads");

  const handleRemoveFile = (intrId) => {
    setUploads((prev) => {
      const copy = { ...prev };
      delete copy[intrId];
      return copy;
    });

    setSelectedFiles((prev) => {
      const copy = { ...prev };
      delete copy[intrId];
      return copy;
    });

    setCreateTicketData((prev) =>
      prev.map((item) => {
        if (item.intr_id == entityDetails?.id) {
          return {
            ...item,
            artifacts: [],
          };
        }
        return item;
      }),
    );
  };

  return (
    <Box sx={{ border: "1px solid #D0D0D0", p: 3, width: "350px" }}>
      {/* 🔹 Loading */}
      {status === "loading" && (
        <Box
          sx={{
            height: "360px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            gap: 2,
          }}
        >
          {/* <Typography>AI suggestion loading...</Typography> */}
          <Typography sx={{ fontSize: "12px", color: "#666" }}>
            {loadingMessage}
          </Typography>
          <LinearProgress
            variant="indeterminate"
            sx={{
              width: "100%",
              height: "5px",
              borderRadius: "0px",
              backgroundColor: "#e0e0e0",
              "& .MuiLinearProgress-bar1Indeterminate": {
                backgroundColor: "#26890D",
                borderRadius: "0px",
              },
              "& .MuiLinearProgress-bar2Indeterminate": {
                backgroundColor: "#26890D",
                borderRadius: "0px",
              },
            }}
          />
        </Box>
      )}

      {/* 🔹 Error */}
      {status === "error" && (
        <Box
          sx={{
            height: "360px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Typography color="error">
            No AI suggestions Found. Please try again.
          </Typography>
        </Box>
      )}

      {/* 🔹 Empty */}
      {status === "empty" && (
        <Box
          sx={{
            height: "360px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Typography>No AI suggestions found.</Typography>
        </Box>
      )}

      {/* 🔹 Success */}
      {status === "success" && (
        <>
          <Typography fontWeight={600} fontSize={16}>
            AI Suggested Artifacts
          </Typography>

          {/* Previous logic rendered all recommendations:
              data?.recommendations?.map((rec, index) => { */}
          {(data?.recommendations?.length
            ? data.recommendations.slice(0, 1)
            : []
          ).map((rec, index) => {
            const intrId = getRecommendationKey(rec, index.toString());
            // const artifactName = getArtifactName(rec);
            const artifactName = "Simulation File";
            const fileData = selectedFiles[intrId];

            return (
              <Box key={intrId} sx={{ mt: 3 }}>
                <Typography fontWeight={500}>
                  {index + 1}. {artifactName}
                </Typography>

                <Button
                  variant="outlined"
                  component="label"
                  size="small"
                  sx={{
                    mt: 1,
                    backgroundColor: "#26890D",
                    color: "white",
                    textTransform: "none",
                    borderRadius: "0px",
                    border: "none",
                    "&:hover": {
                      backgroundColor: "#1f6e0a",
                      border: "none",
                    },
                  }}
                >
                  Choose File
                  <input
                    type="file"
                    hidden
                    onChange={(e) => handleFileChange(rec, e.target.files[0])}
                  />
                </Button>
                <Typography
                  sx={{
                    fontSize: "12px",
                    color: "#666",
                    marginBottom: "16px",
                  }}
                >
                  JPG or PNG. Max size of 800K
                </Typography>
                {uploads[intrId] && (
                  <Box sx={{ marginTop: "16px" }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "6px",
                        backgroundColor: "#f9f9f9",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "12px",
                          color: "#53565A",
                        }}
                      >
                        {uploads[intrId]?.name}
                      </Typography>

                      <IconButton
                        size="small"
                        onClick={() => handleRemoveFile(intrId)}
                      >
                        ✕
                      </IconButton>
                    </Box>

                    {/* Progress Bar */}
                    {uploads[intrId]?.uploading && (
                      <Box sx={{ width: "100%", marginBottom: "8px" }}>
                        <LinearProgress
                          variant="determinate"
                          value={uploads[intrId]?.progress}
                          sx={{
                            height: "5px",
                            borderRadius: "0px",
                            backgroundColor: "#e0e0e0",
                            "& .MuiLinearProgress-bar": {
                              backgroundColor: "#26890D",
                              borderRadius: "0px",
                            },
                          }}
                        />
                      </Box>
                    )}

                    {/* Success */}
                    {uploads[intrId]?.uploaded && (
                      <Typography
                        sx={{
                          fontSize: "12px",
                          color: "#26890D",
                        }}
                      >
                        Successfully Uploaded
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>
            );
          })}
        </>
      )}
      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
        <CustomButtonV2
          size="small"
          handleOnClick={() => setOpen(false)}
          btnName="Close"
        />
      </Box>
    </Box>
  );
}

export default AiSuggestionDecisionMatrix;
